// BasketTable — `.ra-drawer__table` from the redesign bundle.
// design ref: styles.css:3079–3216, bulk-request.jsx:902–989
//
// 4-column grid (resource / status / duration / × remove) shown inside the
// per-recipient drawer group. In `perItem` mode each row expands to add a
// per-row reason textarea.

import { Box, Flex, HStack, Icon, Image, Tooltip, VStack } from "@chakra-ui/react";
import {
  Fragment,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  TbAlertTriangle,
  TbCheck,
  TbChevronDown,
  TbClock,
  TbX,
} from "react-icons/tb";
import StatusPill, { type StatusPillTone } from "components/ds/StatusPill";
import DurationField, { type DurationValue } from "components/ds/DurationField";
import { durationLabel } from "components/ds/durationLabel";
import { checkDurationAgainstMax } from "./durationLimits";
import AppLogo from "components/ds/AppLogo";
import ReasonField from "./ReasonField";
import type { FlatRequestItem } from "./types";
import { catalogLinkName } from "state/members/access_v2/catalogNames";
import type { IdentityOption } from "./identityOptions";
import type { MFRequestableRole } from "state/members/access_v2/catalogs";
import { roleFitNote } from "state/members/access_v2/roleRequests";
import {
  approvalTone,
  itemTerms,
  requestableRoles,
  selectedRoles,
  type RoleSelection,
} from "./roleSelection";

export interface BasketItemCfg {
  duration: DurationValue;
  reason: string;
}

// Per-table hook for the inline account ("as …") picker. Supplied only for the
// self recipient group (see BasketDrawer); undefined ⇒ no picker rendered.
export interface IdentityControl {
  getOptions: (itemId: string) => IdentityOption[];
  getSelected: (itemId: string) => string[];
  onToggle: (itemId: string, identityId: string) => void;
  problem: (itemId: string) => string | null;
}

export type BasketTableLayout = "full" | "compact" | "card";

interface BasketTableProps {
  items: FlatRequestItem[];
  perItem: boolean;
  /** Density: "full" is the 4-column grid, "compact" merges status + duration
   *  into one stacked cell, "card" (the split tier's list column) drops the
   *  table entirely — each basket item renders as a flat filled card: logo +
   *  remove header, app breadcrumb over the resource name, then labelled
   *  status/duration rows. */
  layout?: BasketTableLayout;
  /** Returns the config for an item — either bulk fallback or per-item override. */
  getCfg: (itemId: string) => BasketItemCfg;
  /** Patches a per-item override (only called when perItem=true). */
  patchCfg: (itemId: string, patch: Partial<BasketItemCfg>) => void;
  /** A specific account's effective duration (per-account override or the
   *  resource's) — each fanned-out account is its own request. */
  getAccountDuration: (itemId: string, identityId: string) => DurationValue;
  patchAccountDuration: (
    itemId: string,
    identityId: string,
    duration: DurationValue,
  ) => void;
  onRemove: (itemId: string) => void;
  roleSelection: RoleSelection;
  /** A ticked role's effective duration on this recipient's row (the account
   *  only decides it when the role has no override of its own). */
  getRoleDuration: (
    item: FlatRequestItem,
    role: MFRequestableRole,
    identityId?: string,
  ) => DurationValue;
  patchRoleDuration: (
    item: FlatRequestItem,
    roleId: string,
    duration: DurationValue,
  ) => void;
  /**
   * Per-row "this row won't submit" reasons (typically: requested duration
   * exceeds the resource's policy max). When set, the row is dimmed + a
   * warning chip is shown so the user can see what's going to be skipped
   * BEFORE clicking Send.
   */
  rowWarnings?: Record<string, string | undefined>;
  /** Rows a group sends to only some of its members, and why the rest miss. */
  rowNotes?: Record<string, string | undefined>;
  /**
   * Inline account picker (PLO-4704). Present only for the self recipient; a
   * row renders the "as <account> ▾" line when its item has >1 eligible
   * account.
   */
  identity?: IdentityControl;
}

const HEADER_CELL = {
  display: "flex",
  alignItems: "center",
  fontSize: "xs", // 12px
  fontWeight: 700,
  color: "neutral.950",
  minW: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
} as const;

const GRID_TEMPLATE = "minmax(0, 1fr) 120px 140px 32px";
// Compact (narrow list column): status + duration merge into one stacked cell
// so the resource name keeps a workable share of the row.
// The middle column is a fixed width, as in the full layout, so a row's
// duration and its roles' durations (separate grids) share a left edge.
const GRID_TEMPLATE_COMPACT = "minmax(0, 1fr) 140px 32px";

// Lines a role name up with the resource name: past the lg AppLogo (40px) and
// ResourceCell's md gap.
const ROLE_NAME_INDENT = "calc(var(--chakra-space-3xl) + var(--chakra-space-md))";

// The identity's SOURCE — the IdP/integration the account lives on (Google,
// Microsoft, Okta, …), from `intImageUrl`. Signifies where the grant lands far
// better than a generic key. Rendered in a 16px elevated tile so the raw
// full-bleed brand SVGs read as neat, consistent logo chips everywhere.
const SourceIcon = ({ src }: { src?: string }) => (
  <Flex
    align="center"
    justify="center"
    boxSize="16px"
    flexShrink={0}
    borderRadius="4px"
    bg="neutral.0"
    boxShadow="elevLow"
    overflow="hidden"
  >
    {src ? (
      <Image src={src} boxSize="11px" objectFit="contain" alt="" />
    ) : (
      <Box boxSize="8px" borderRadius="2px" bg="neutral.300" />
    )}
  </Flex>
);

const BasketTable = ({
  items,
  perItem,
  layout = "full",
  getCfg,
  patchCfg,
  getAccountDuration,
  patchAccountDuration,
  onRemove,
  roleSelection,
  getRoleDuration,
  patchRoleDuration,
  rowWarnings = {},
  rowNotes = {},
  identity,
}: BasketTableProps) => {
  // Per-row "the user has left this reason field" tracking — the red required
  // error only shows after blur, not the moment a flagged row appears.
  const [touchedIds, setTouchedIds] = useState<Set<string>>(() => new Set());
  // Which row's account picker is open, and the layout it was in when opened.
  // Lifted out of IdentityLine so the row can hold the SAME picker mounted
  // across a multi-select and only reshape once the picker closes.
  const [openState, setOpenState] = useState<{
    itemId: string;
    expanded: boolean;
  } | null>(null);
  const compact = layout === "compact";
  const stacked = layout === "card";
  return (
    <Box
      borderWidth={stacked ? "0" : "1px"}
      borderStyle="solid"
      borderColor="neutral.200"
      borderRadius="component.lg" // 8px
      bg={stacked ? "transparent" : "neutral.0"}
    >
      {/* .ra-tbl__head — no header in card mode: each card carries its own
          labels, so a "Resource"-only strip would just repeat itself. */}
      {!stacked && (
        <Box
          display="grid"
          gridTemplateColumns={compact ? GRID_TEMPLATE_COMPACT : GRID_TEMPLATE}
          gap="10px"
          px="md" // 12px
          py="sm" // 8px
          bg="neutral.50"
          borderTopLeftRadius="component.md1" // 7px (matches design)
          borderTopRightRadius="component.md1"
          borderBottomWidth="1px"
          borderBottomStyle="solid"
          borderBottomColor="neutral.200"
        >
          <Box {...HEADER_CELL}>Resource</Box>
          <Box {...HEADER_CELL}>Status</Box>
          {!compact && <Box {...HEADER_CELL}>Duration</Box>}
          <Box />
        </Box>
      )}

      {/* .ra-tbl__body — card mode separates items with gaps, not dividers */}
      <Box display="flex" flexDirection="column" gap={stacked ? "sm" : 0}>
        {items.map((item, idx) => {
          const cfg = getCfg(item.id);
          const terms = itemTerms(item, roleSelection);
          const last = idx === items.length - 1;
          // PLO-4704 — when a resource is requested for MORE than one account,
          // it stops being a single row and expands into a parent + one
          // sub-row per account (each its own status/duration/remove).
          const accountOptions = identity?.getOptions(item.id) ?? [];
          const selectedAccounts = identity?.getSelected(item.id) ?? [];
          // A multi-account resource collapsed onto ONE account: that account's
          // duration IS the row's duration, so edit it per-account here too —
          // keeps the collapsed field in step with the expanded sub-row and the
          // submitted request. (Single-account resources use the resource cfg.)
          const soleAccount =
            accountOptions.length > 1 && selectedAccounts.length === 1
              ? selectedAccounts[0]
              : undefined;
          const wantExpanded =
            Boolean(identity) &&
            accountOptions.length > 1 &&
            selectedAccounts.length >= 2;
          const pickerOpen = openState?.itemId === item.id;
          // Freeze the layout while this row's picker is open so selecting a
          // second account doesn't reshape the row (and unmount the open
          // dropdown) mid-click; it settles to `wantExpanded` on close.
          const expanded = pickerOpen ? openState.expanded : wantExpanded;
          const onPickerOpenChange = (o: boolean) =>
            setOpenState(o ? { itemId: item.id, expanded: wantExpanded } : null);
          // A multi-account resource with nothing chosen is incomplete — flag it
          // like any other invalid row ("won't be sent") whenever it's empty, so
          // the badge tracks the actual selection rather than the picker being
          // open. The in-menu hint guides while picking, and the row only dims
          // once the picker is closed (see opacity below) to keep it clear.
          const needsAccount =
            accountOptions.length > 1 && selectedAccounts.length === 0;
          // A chosen account whose duration exceeds the resource max won't be
          // sent (buildBulkPayloads drops that account). Flag it on the self
          // recipient's row — identity is self-only, so non-self rows have no
          // selected accounts and never reach this.
          const overMaxReason =
            perItem && selectedAccounts.length > 0
              ? selectedAccounts
                  .map((accId) =>
                    checkDurationAgainstMax(
                      getAccountDuration(item.id, accId),
                      terms,
                    ),
                  )
                  .find((c) => c.exceeds && c.reason)?.reason
              : undefined;
          const warning =
            rowWarnings[item.id] ??
            identity?.problem(item.id) ??
            (needsAccount
              ? "Choose an account"
              : (overMaxReason ?? undefined));
          const isInvalid = Boolean(warning);
          const reasonRequired = terms.requiresComment;
          const tone = approvalTone(item, roleSelection);
          // Each account is its own request and a role with no duration of
          // its own takes the account's, so customising lists the roles under
          // each account.
          const rolesFor = (accountId: string | undefined) =>
            requestableRoles(item) ? (
              <RoleDurations
                roles={selectedRoles(item, roleSelection)}
                perItem={perItem}
                layout={layout}
                requested={
                  accountId
                    ? getAccountDuration(item.id, accountId)
                    : cfg.duration
                }
                getDuration={(role) => getRoleDuration(item, role, accountId)}
                onChange={(roleId, d) => patchRoleDuration(item, roleId, d)}
              />
            ) : null;
          const rolesEl = rolesFor(soleAccount ?? selectedAccounts[0]);
          const note = rowNotes[item.id];
          const noteEl = note ? (
            <Flex
              align="center"
              gap="2xs"
              px={stacked ? 0 : "md"}
              pb={stacked ? 0 : "2.5"}
              mt={stacked ? "sm" : 0}
              fontSize="xs"
              color="warning.800"
            >
              <Icon as={TbAlertTriangle} boxSize="12px" flexShrink={0} />
              <Box>{note}</Box>
            </Flex>
          ) : null;
          // A duration-warned row is dropped anyway, so don't also nag for a
          // reason on it. Only flag red once the field's been touched.
          const showReasonError =
            perItem &&
            reasonRequired &&
            !isInvalid &&
            cfg.reason.trim() === "" &&
            touchedIds.has(item.id);
          // Status — replaced by a warning chip when the row is invalid; the
          // StatusPill ("Auto-approve" / "Needs approval") becomes irrelevant
          // once we know the row won't be sent.
          const statusEl = isInvalid ? (
            <RowWarningChip reason={warning!} />
          ) : (
            <StatusPill tone={tone} />
          );
          // A role request sends only its roles' durations, each edited on its
          // role row, so customising it has no duration of its own to edit.
          const roleRequest = Boolean(requestableRoles(item));
          const durationEl = !perItem ? (
            <DurationText value={cfg.duration} />
          ) : roleRequest ? null : (
            <DurationField
              size="sm"
              value={
                soleAccount
                  ? getAccountDuration(item.id, soleAccount)
                  : cfg.duration
              }
              allowIndefinite={terms.allowIndefinite}
              onChange={(v) =>
                soleAccount
                  ? patchAccountDuration(item.id, soleAccount, v)
                  : patchCfg(item.id, { duration: v })
              }
            />
          );
          const accountDurationEl = (accountId: string) =>
            !perItem ? (
              <DurationText value={cfg.duration} />
            ) : roleRequest ? null : (
              <DurationField
                size="sm"
                value={getAccountDuration(item.id, accountId)}
                allowIndefinite={terms.allowIndefinite}
                onChange={(v) => patchAccountDuration(item.id, accountId, v)}
              />
            );
          const identityLineEl =
            identity && accountOptions.length >= 1 ? (
              <IdentityLine
                options={accountOptions}
                selectedIds={selectedAccounts}
                onToggle={(id) => identity.onToggle(item.id, id)}
                open={pickerOpen}
                onOpenChange={onPickerOpenChange}
              />
            ) : undefined;
          const resourceCell = (
            <ResourceCell item={item} identityLine={identityLineEl} />
          );
          const removeEl = (
            <Box
              as="button"
              type="button"
              aria-label={`Remove ${catalogLinkName(item.resource)}`}
              onClick={() => onRemove(item.id)}
              w="32px"
              h="32px"
              borderRadius="component.md" // 6px
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              color="neutral.500"
              bg="transparent"
              border="none"
              cursor="pointer"
              flexShrink={0}
              transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
              _hover={{ bg: "neutral.100", color: "error.600" }}
              _active={{ transform: "scale(0.96)" }}
            >
              <Icon as={TbX} boxSize="14px" />
            </Box>
          );
          return (
            <Box
              key={item.id}
              borderBottomWidth={stacked || last ? "0" : "1px"}
              borderBottomStyle="solid"
              borderBottomColor="neutral.200"
              // Card mode: same surface language as CatalogCard — white with
              // elevation-low, no fill.
              bg={stacked ? "neutral.0" : undefined}
              boxShadow={stacked ? "elevLow" : undefined}
              borderRadius={stacked ? "component.2xl" : undefined} // 12px
              p={stacked ? "lg" : undefined} // 16px
              // Dim invalid rows so it's visually obvious they won't be
              // submitted. The × remove button stays at full opacity (it's
              // outside the dimmed group below) so the user can still
              // remove the row.
            >
              {expanded && identity ? (
                stacked ? (
                  <CardExpandedResource
                    item={item}
                    cfg={cfg}
                    identityLine={identityLineEl}
                    selectedOpts={accountOptions.filter((o) =>
                      selectedAccounts.includes(o.id),
                    )}
                    onToggleAccount={(id) => identity.onToggle(item.id, id)}
                    onRemoveResource={() => onRemove(item.id)}
                    perItem={perItem}
                    tone={tone}
                    accountDuration={accountDurationEl}
                    reasonRequired={reasonRequired}
                    showReasonError={showReasonError}
                    onReasonChange={(v) => patchCfg(item.id, { reason: v })}
                    onReasonBlur={() =>
                      setTouchedIds((prev) => new Set(prev).add(item.id))
                    }
                    roles={perItem ? null : rolesEl}
                    accountRoles={perItem ? rolesFor : undefined}
                  />
                ) : (
                <ExpandedResource
                  item={item}
                  compact={compact}
                  cfg={cfg}
                  options={accountOptions}
                  selectedIds={selectedAccounts}
                  onToggle={(id) => identity.onToggle(item.id, id)}
                  open={pickerOpen}
                  onOpenChange={onPickerOpenChange}
                  onRemoveResource={() => onRemove(item.id)}
                  tone={tone}
                  perItem={perItem}
                  accountDuration={accountDurationEl}
                  reasonRequired={reasonRequired}
                  showReasonError={showReasonError}
                  onReasonChange={(v) => patchCfg(item.id, { reason: v })}
                  onReasonBlur={() =>
                    setTouchedIds((prev) => new Set(prev).add(item.id))
                  }
                  roles={perItem ? null : rolesEl}
                  accountRoles={perItem ? rolesFor : undefined}
                />
                )
              ) : (
                <>
              {stacked ? (
                /* Card: logo + remove header, app breadcrumb over the
                   resource name, then labelled status/duration rows. */
                <Box
                  fontSize="smd"
                  opacity={isInvalid && !pickerOpen ? 0.55 : 1}
                  transition="opacity 120ms cubic-bezier(0.23, 1, 0.32, 1)"
                >
                  <CardHeader
                    item={item}
                    identityLine={identityLineEl}
                    remove={removeEl}
                  />
                  <VStack align="stretch" spacing="1xs" mt="sm">
                    <CardLabelRow label="status:">{statusEl}</CardLabelRow>
                    {durationEl && (
                      <CardLabelRow label="duration:">{durationEl}</CardLabelRow>
                    )}
                  </VStack>
                  {rolesEl}
                  {noteEl}
                </Box>
              ) : (
              <Box
                display="grid"
                gridTemplateColumns={
                  compact ? GRID_TEMPLATE_COMPACT : GRID_TEMPLATE
                }
                gap="10px"
                alignItems="flex-start"
                px="md"
                py="2.5"
                fontSize="smd"
                // Apply opacity to the row content but NOT the remove button —
                // wrapping the first three cells in a group would change the
                // grid; instead we just dim the whole row but boost the ×
                // back up via its own opacity override.
                opacity={isInvalid && !pickerOpen ? 0.55 : 1}
                transition="opacity 120ms cubic-bezier(0.23, 1, 0.32, 1)"
              >
                {/* Resource cell — with the account selector stacked under the
                    resource name (shown whenever there's an eligible account). */}
                {resourceCell}

                {/* Compact: status + duration stack in one merged cell so the
                    resource keeps a workable share of the row. */}
                <StatusDurationCells
                  compact={compact}
                  status={statusEl}
                  duration={durationEl}
                />

                {/* Remove × — 32×32 hit target (max we can give it inside
                    the table row; 40 is the design-eng ideal but would
                    visually bloat the row). Icon stays 14px. */}
                <Flex justify="flex-end">{removeEl}</Flex>
              </Box>
              )}
              {!stacked && rolesEl}
              {!stacked && noteEl}

              {perItem && (
                // .ra-tbl__reason — pad right by 54px to align with duration
                // column; card mode is padded by the card itself.
                <Box
                  pl={stacked ? 0 : "md"}
                  pr={stacked ? 0 : compact ? "42px" : "54px"}
                  pb={stacked ? 0 : "2.5"}
                  mt={stacked ? "sm" : 0}
                >
                  <ReasonField
                    required={reasonRequired}
                    error={showReasonError}
                    value={cfg.reason}
                    onChange={(v) => patchCfg(item.id, { reason: v })}
                    onBlur={() =>
                      setTouchedIds((prev) => new Set(prev).add(item.id))
                    }
                    rows={2}
                    barText="A reason is required for this resource."
                    placeholder={
                      reasonRequired
                        ? "Add a reason for the approver"
                        : "Add reasoning…"
                    }
                  />
                </Box>
              )}
                </>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// Bulk-mode duration readout. Duration number changes as the user toggles
// presets — tabular-nums prevents a layout shift when "1" becomes "3 months".
const DurationText = ({ value }: { value: DurationValue }) => (
  <Flex
    align="center"
    gap="1xs"
    fontSize="smd"
    color="neutral.800"
    sx={{ fontVariantNumeric: "tabular-nums" }}
  >
    <Icon as={TbClock} boxSize="14px" color="neutral.600" />
    {durationLabel(value)}
  </Flex>
);

// The status + duration cells of a row: two grid columns normally, one merged
// stacked cell in compact mode.
const StatusDurationCells = ({
  compact,
  status,
  duration,
}: {
  compact: boolean;
  status: ReactNode;
  duration: ReactNode;
}) =>
  compact ? (
    <VStack align="flex-start" spacing="1xs" minW={0} py="2px">
      {status}
      {duration}
    </VStack>
  ) : (
    <>
      <Box display="flex" alignItems="center">
        {status}
      </Box>
      <Box display="flex" alignItems="center" minW={0}>
        {duration}
      </Box>
    </>
  );

// Card-mode building blocks — the split tier's list column renders each
// basket item as a flat filled card instead of a table row.

// Logo + remove header, app breadcrumb over the resource name, identity line.
const CardHeader = ({
  item,
  identityLine,
  remove,
}: {
  item: FlatRequestItem;
  identityLine?: ReactNode;
  remove: ReactNode;
}) => {
  const appName = item.catalogItem.name?.trim();
  const rawResourceName = item.resource.name?.trim();
  const resourceName = catalogLinkName(item.resource, rawResourceName);
  // Same rule as ResourceCell: hide the app breadcrumb when it's absent or
  // would just duplicate the resource name.
  const showAppPrefix = Boolean(appName) && appName !== rawResourceName;
  return (
    <>
      <Flex align="flex-start" justify="space-between" gap="sm">
        <AppLogo
          size="lg"
          name={appName || resourceName || "?"}
          logoUrl={item.catalogItem.imageUrl || undefined}
        />
        {remove}
      </Flex>
      {showAppPrefix && (
        <Box
          mt="sm"
          fontSize="xs"
          color="neutral.600"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {appName}
        </Box>
      )}
      <Box
        mt={showAppPrefix ? "2px" : "sm"}
        fontSize="md"
        fontWeight={600}
        color="neutral.950"
        lineHeight="1.3"
        sx={{ overflowWrap: "anywhere" }}
      >
        {resourceName}
      </Box>
      {identityLine && <Box mt="2px">{identityLine}</Box>}
    </>
  );
};

// "status:" / "duration:" labelled row — labels share a left edge so the
// values line up down the card.
const CardLabelRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <Flex align="center" gap="sm" minH="24px">
    <Box color="neutral.600" minW="64px" flexShrink={0}>
      {label}
    </Box>
    <Box minW={0}>{children}</Box>
  </Flex>
);

const RemoveIconButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <Box
    as="button"
    type="button"
    aria-label={label}
    onClick={onClick}
    w="32px"
    h="32px"
    borderRadius="component.md" // 6px
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    color="neutral.500"
    bg="transparent"
    border="none"
    cursor="pointer"
    flexShrink={0}
    transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
    _hover={{ bg: "neutral.100", color: "error.600" }}
    _active={{ transform: "scale(0.96)" }}
  >
    <Icon as={TbX} boxSize="14px" />
  </Box>
);

// Card-mode counterpart of ExpandedResource — a resource requested for >1
// account. One header, then a divided block per selected account with its own
// labelled status/duration rows and remove.
const CardExpandedResource = ({
  item,
  tone,
  cfg,
  identityLine,
  selectedOpts,
  onToggleAccount,
  onRemoveResource,
  perItem,
  accountDuration,
  reasonRequired,
  showReasonError,
  onReasonChange,
  onReasonBlur,
  roles,
  accountRoles,
}: {
  item: FlatRequestItem;
  tone: StatusPillTone;
  cfg: BasketItemCfg;
  identityLine?: ReactNode;
  selectedOpts: IdentityOption[];
  onToggleAccount: (identityId: string) => void;
  onRemoveResource: () => void;
  perItem: boolean;
  accountDuration: (identityId: string) => ReactNode;
  reasonRequired: boolean;
  showReasonError: boolean;
  onReasonChange: (v: string) => void;
  onReasonBlur: () => void;
  roles: ReactNode;
  accountRoles?: (accountId: string) => ReactNode;
}) => {
  return (
    <Box fontSize="smd">
      <CardHeader
        item={item}
        identityLine={identityLine}
        remove={
          <RemoveIconButton
            label={`Remove ${catalogLinkName(item.resource)}`}
            onClick={onRemoveResource}
          />
        }
      />
      {selectedOpts.map((opt) => {
        const duration = accountDuration(opt.id);
        return (
          <Box
            key={opt.id}
            mt="sm"
            pt="sm"
            borderTopWidth="1px"
            borderTopStyle="solid"
            borderTopColor="neutral.200"
          >
            <Flex align="center" gap="sm">
              <SourceIcon src={opt.imageUrl} />
              <Box
                flex="1"
                minW={0}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                fontWeight={500}
                color="neutral.900"
              >
                {opt.label}
              </Box>
              <RemoveIconButton
                label={`Remove ${opt.label}`}
                onClick={() => onToggleAccount(opt.id)}
              />
            </Flex>
            <VStack align="stretch" spacing="1xs" mt="1xs" pl="24px">
              <CardLabelRow label="status:">
                <StatusPill tone={tone} />
              </CardLabelRow>
              {duration && (
                <CardLabelRow label="duration:">{duration}</CardLabelRow>
              )}
            </VStack>
            {accountRoles && <Box pl="24px">{accountRoles(opt.id)}</Box>}
          </Box>
        );
      })}
      {roles}
      {perItem && (
        <Box mt="sm">
          <ReasonField
            required={reasonRequired}
            error={showReasonError}
            value={cfg.reason}
            onChange={onReasonChange}
            onBlur={onReasonBlur}
            rows={2}
            barText="A reason is required for this resource."
            placeholder={
              reasonRequired ? "Add a reason for the approver" : "Add reasoning…"
            }
          />
        </Box>
      )}
    </Box>
  );
};

// The ticked roles of a resource whose access follows them. Customising, each
// role is a sub-row with its own duration, fitted to its cap; otherwise the
// row's duration applies to every role, and a role it doesn't fit says what it
// gets instead.
const RoleDurations = ({
  roles,
  perItem,
  layout,
  requested,
  getDuration,
  onChange,
}: {
  roles: MFRequestableRole[];
  perItem: boolean;
  layout: BasketTableLayout;
  requested: DurationValue;
  getDuration: (role: MFRequestableRole) => DurationValue;
  onChange: (roleId: string, duration: DurationValue) => void;
}) => {
  const stacked = layout === "card";
  const compact = layout === "compact";
  if (!perItem) {
    const notes = roles
      .map((role) => roleFitNote(role, requested))
      .filter((n): n is string => Boolean(n));
    if (notes.length === 0) return null;
    return (
      <VStack
        align="stretch"
        spacing="2px"
        px={stacked ? 0 : "md"}
        pb={stacked ? 0 : "2.5"}
        mt={stacked ? "sm" : 0}
        fontSize="xs"
        color="neutral.600"
      >
        {notes.map((note) => (
          <Box key={note}>{note}</Box>
        ))}
      </VStack>
    );
  }
  return (
    <VStack
      align="stretch"
      spacing="1xs"
      px={stacked ? 0 : "md"}
      pb={stacked ? 0 : "2.5"}
      mt={stacked ? "sm" : 0}
      fontSize="smd"
    >
      {roles.map((role) => {
        const name = (
          <Box
            minW={0}
            pl={stacked ? 0 : ROLE_NAME_INDENT}
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            color="neutral.900"
            fontWeight={500}
          >
            {role.name}
          </Box>
        );
        const field = (
          <DurationField
            size="sm"
            value={getDuration(role)}
            allowIndefinite={
              role.terms.allowIndefinite || role.terms.indefiniteOnly
            }
            onChange={(d) => onChange(role.id, d)}
          />
        );
        return stacked ? (
          <CardLabelRow key={role.id} label={`${role.name}:`}>
            {field}
          </CardLabelRow>
        ) : (
          <Box
            key={role.id}
            display="grid"
            gridTemplateColumns={
              compact ? GRID_TEMPLATE_COMPACT : GRID_TEMPLATE
            }
            gap="10px"
            alignItems="center"
          >
            <Box gridColumn={compact ? "1 / 2" : "1 / 3"} minW={0}>
              {name}
            </Box>
            <Box minW={0}>{field}</Box>
          </Box>
        );
      })}
    </VStack>
  );
};

// Warning chip swapped into the Status column when a basket row won't
// submit. Same amber palette as the catalog-card warning chip so the two
// surfaces feel like one feature, not two separate warnings.
const RowWarningChip = ({ reason }: { reason: string }) => (
  <Tooltip
    label={reason}
    placement="top"
    hasArrow
    openDelay={150}
    bg="neutral.950"
    color="neutral.0"
    fontSize="xs"
    px="10px"
    py="1xs"
    borderRadius="component.md"
  >
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="2xs"
      px="2xs"
      py="2px"
      borderRadius="full"
      bg="warning.100"
      color="warning.800"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="warning.200"
      fontSize="xs"
      fontWeight={500}
      lineHeight="1"
      cursor="help"
      // Tooltip wrapper applies opacity to descendants — boost back to 1
      // so the chip stays readable even when the row is dimmed.
      opacity={1}
    >
      <Icon as={TbAlertTriangle} boxSize="12px" />
      Won't be sent
    </Box>
  </Tooltip>
);

const ResourceCell = ({
  item,
  identityLine,
}: {
  item: FlatRequestItem;
  // The "as <account>" selector, stacked under the resource name inside the
  // cell (4px gap) so the whole thing reads as one row. The 32px logo spans it.
  identityLine?: ReactNode;
}) => {
  const logoUrl = item.catalogItem.imageUrl;
  const appName = item.catalogItem.name?.trim();
  const rawResourceName = item.resource.name?.trim();
  const resourceName = catalogLinkName(item.resource, rawResourceName);
  // Hide the "App / Resource" framing when the app row has no name OR when
  // it would just duplicate the resource (some orgs use the same string
  // for both). Avoids the bare-slash artifact and the "Slack / Slack"
  // double-print.
  const showAppPrefix = Boolean(appName) && appName !== rawResourceName;
  const fullLabel = showAppPrefix ? `${appName} / ${resourceName}` : resourceName;

  // Truncation-aware tooltip: only enable Chakra's Tooltip when at least
  // one segment is actually clipped. Avoids the "tooltip repeats what's
  // already on screen" hover noise on cells that fit. Watched via
  // ResizeObserver on the row container so resizing the drawer flips the
  // truncated state correctly.
  const rowRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  useLayoutEffect(() => {
    const root = rowRef.current;
    if (!root) return;
    const measure = () => {
      const segments = root.querySelectorAll<HTMLElement>("[data-truncatable]");
      let truncated = false;
      segments.forEach((el) => {
        if (el.scrollWidth > el.clientWidth + 1) truncated = true;
      });
      setIsTruncated(truncated);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    return () => ro.disconnect();
  }, [appName, resourceName]);

  return (
    <HStack align="flex-start" gap="md" minW={0}>
      <AppLogo
        size="lg"
        name={appName || resourceName || "?"}
        logoUrl={logoUrl || undefined}
      />
      <VStack align="stretch" spacing="0" minW={0}>
        <Tooltip
          label={fullLabel}
          placement="top"
          hasArrow
          openDelay={250}
          isDisabled={!isTruncated}
          bg="neutral.950"
          color="neutral.0"
          fontSize="xs"
          px="10px"
          py="1xs"
          borderRadius="component.md"
        >
          <Flex ref={rowRef} align="center" gap="1xs" minW={0} flexWrap="nowrap">
            {showAppPrefix && (
              <>
                <Box
                  as="span"
                  data-truncatable
                  color="neutral.700"
                  fontWeight={500}
                  // The prefix (app) absorbs (nearly) all the shrink — a much
                  // higher flex-shrink than the resource — so a tight cell
                  // collapses to "…/Resource" and keeps the LAST, most-specific
                  // segment legible. Resource only ellipsizes once app collapses.
                  flex="0 200 auto"
                  minW={0}
                  whiteSpace="nowrap"
                  overflow="hidden"
                  textOverflow="ellipsis"
                >
                  {appName}
                </Box>
                <Box as="span" color="neutral.400" flexShrink={0}>
                  /
                </Box>
              </>
            )}
            <Box
              as="span"
              data-truncatable
              color="neutral.950"
              fontWeight={600}
              flex="0 1 auto"
              minW={0}
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {resourceName}
            </Box>
          </Flex>
        </Tooltip>
        {identityLine}
      </VStack>
    </HStack>
  );
};

// Inline "as <account> ▾" line under the resource name — PLO-4704. Rendered
// only when a resource has >1 eligible account, so the row stays clean in the
// common single-account case (no extra column). It is MULTI-select: a resource
// can be requested for several of the requester's accounts at once (each
// becomes its own request). Accounts that already hold the access are shown
// disabled ("Already has access"); the row defaults to one that needs it.
const IdentityLine = ({
  options,
  selectedIds,
  onToggle,
  open,
  onOpenChange,
}: {
  options: IdentityOption[];
  selectedIds: string[];
  onToggle: (identityId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const wrapRef = useRef<HTMLDivElement>(null);

  const selectedOpts = options.filter((o) => selectedIds.includes(o.id));
  const primary = selectedOpts[0] ?? options[0];
  const extra = selectedOpts.length - 1;

  // Click outside closes the menu.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        onOpenChange(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, onOpenChange]);

  // Show the full account label on hover, but only when it's actually clipped
  // (mirrors the resource-name tooltip) so a long extId stays recoverable.
  const labelRef = useRef<HTMLSpanElement>(null);
  const [labelTruncated, setLabelTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = labelRef.current;
    if (!el) return;
    const measure = () => setLabelTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [primary?.label]);

  // A single eligible account still gets shown — for transparency about where
  // the grant lands — but as a static, informational line (no dropdown).
  const interactive = options.length > 1;
  if (!interactive) {
    // Tooltip wraps the whole line (not the truncating span directly) — mirrors
    // the resource-name cell above. Wrapping Chakra's Tooltip around the
    // flex-shrinking, truncated span itself made the ellipsis collapse whenever
    // the span took interaction (the tooltip trigger reshaped the element).
    return (
      <Tooltip
        label={primary?.label}
        placement="top"
        hasArrow
        openDelay={250}
        isDisabled={!labelTruncated}
        bg="neutral.950"
        color="neutral.0"
        fontSize="xs"
        px="10px"
        py="1xs"
        borderRadius="component.md"
      >
        <Flex
          align="center"
          gap="4px"
          py="3px"
          minW={0}
          maxW="100%"
          fontSize="xs"
          color="neutral.700"
          // Non-selectable: an overflow:hidden ellipsis element is still
          // scrollable, so clicking it seeds a text selection and the browser
          // scrolls the clipped chars into view (dropping the ellipsis) until
          // you click away. The full value stays recoverable via the tooltip.
          userSelect="none"
        >
          <Box as="span" flexShrink={0}>
            as
          </Box>
          <Box
            as="span"
            ref={labelRef}
            fontWeight={600}
            color="neutral.950"
            minW={0}
            isTruncated
          >
            {primary?.label}
          </Box>
        </Flex>
      </Tooltip>
    );
  }

  return (
    <Box ref={wrapRef} position="relative" ml="-6px" w="fit-content" maxW="100%">
      <Flex
        as="button"
        type="button"
        align="center"
        gap="4px"
        px="6px"
        py="3px"
        minW={0}
        maxW="100%"
        borderRadius="component.md"
        color="neutral.700"
        fontSize="xs"
        transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1)"
        _hover={{ bg: "neutral.100" }}
        onClick={() => onOpenChange(!open)}
        aria-label="Change which accounts receive this access"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {selectedOpts.length > 0 ? (
          <>
            <Box as="span" flexShrink={0}>
              as
            </Box>
            {/* Azurite (primary/brand) so the interactive selector reads as a
                link. Truncates rather than overflowing the cell — narrow list
                columns were letting a long extId collide with the next cell. */}
            <Box
              as="span"
              fontWeight={600}
              color="text.body.azurite"
              whiteSpace="nowrap"
              minW={0}
              overflow="hidden"
              textOverflow="ellipsis"
            >
              {selectedOpts[0].label}
            </Box>
            {extra > 0 && (
              <Box as="span" flexShrink={0} color="neutral.500">
                +{extra}
              </Box>
            )}
          </>
        ) : (
          <Box
            as="span"
            fontWeight={600}
            color="warning.700"
            whiteSpace="nowrap"
          >
            Select an account
          </Box>
        )}
        <Icon
          as={TbChevronDown}
          boxSize="12px"
          color="text.body.azurite"
          flexShrink={0}
        />
      </Flex>

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={30}
          minW="248px"
          bg="neutral.0"
          borderWidth="1px"
          borderStyle="solid"
          borderColor="neutral.200"
          borderRadius="component.lg"
          boxShadow="0 12px 28px -8px rgba(16,24,40,0.18), 0 4px 10px -6px rgba(16,24,40,0.12)"
          py="1xs"
          role="menu"
          aria-multiselectable="true"
        >
          <Box
            px="md"
            pt="2px"
            pb="1xs"
            fontSize="xs"
            fontWeight={600}
            color="neutral.500"
          >
            Grant access to
          </Box>
          {options.map((opt) => {
            const isSelected = selectedIds.includes(opt.id);
            const disabled = Boolean(opt.alreadyHasAccess);
            return (
              <Flex
                key={opt.id}
                as="button"
                type="button"
                role="menuitemcheckbox"
                aria-checked={isSelected}
                disabled={disabled}
                align="center"
                gap="sm"
                w="full"
                px="md"
                py="1xs"
                textAlign="left"
                bg="transparent"
                border="none"
                cursor={disabled ? "not-allowed" : "pointer"}
                opacity={disabled ? 0.55 : 1}
                transition="background-color 100ms ease-out"
                _hover={disabled ? undefined : { bg: "neutral.50" }}
                onClick={() => {
                  if (disabled) return;
                  onToggle(opt.id);
                  // Stay open — it's multi-select.
                }}
              >
                {/* Checkbox box — filled when selected. */}
                <Flex
                  align="center"
                  justify="center"
                  boxSize="16px"
                  flexShrink={0}
                  borderRadius="4px"
                  borderWidth="1.5px"
                  borderStyle="solid"
                  borderColor={isSelected ? "p.600" : "neutral.300"}
                  bg={isSelected ? "p.600" : "transparent"}
                >
                  {isSelected && (
                    <Icon as={TbCheck} boxSize="12px" color="neutral.0" />
                  )}
                </Flex>
                <SourceIcon src={opt.imageUrl} />
                <Box flex="1" minW={0}>
                  <Box
                    fontSize="smd"
                    fontWeight={500}
                    color="neutral.950"
                    whiteSpace="nowrap"
                    overflow="hidden"
                    textOverflow="ellipsis"
                  >
                    {opt.label}
                  </Box>
                  {(disabled || opt.subtitle) && (
                    <Box fontSize="xs" color="neutral.500">
                      {disabled ? "Already has access" : opt.subtitle}
                    </Box>
                  )}
                </Box>
              </Flex>
            );
          })}
          {selectedIds.length === 0 && (
            <Box px="md" pt="1xs" pb="2px" fontSize="xs" color="warning.700">
              Select at least one account
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// Expanded layout for a resource requested for MORE than one account — PLO-4704.
// A header (resource + the account picker + remove-whole-resource) followed by
// one sub-row per selected account, each with its own status, duration and
// remove.
const ExpandedResource = ({
  item,
  tone,
  compact,
  cfg,
  options,
  selectedIds,
  onToggle,
  open,
  onOpenChange,
  onRemoveResource,
  perItem,
  accountDuration,
  reasonRequired,
  showReasonError,
  onReasonChange,
  onReasonBlur,
  roles,
  accountRoles,
}: {
  item: FlatRequestItem;
  tone: StatusPillTone;
  compact: boolean;
  cfg: BasketItemCfg;
  options: IdentityOption[];
  selectedIds: string[];
  onToggle: (identityId: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRemoveResource: () => void;
  perItem: boolean;
  accountDuration: (identityId: string) => ReactNode;
  reasonRequired: boolean;
  showReasonError: boolean;
  onReasonChange: (v: string) => void;
  onReasonBlur: () => void;
  roles: ReactNode;
  accountRoles?: (accountId: string) => ReactNode;
}) => {
  const selectedOpts = options.filter((o) => selectedIds.includes(o.id));

  const removeButton = (label: string, onClick: () => void) => (
    <Box
      as="button"
      type="button"
      aria-label={label}
      onClick={onClick}
      w="32px"
      h="32px"
      borderRadius="component.md"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      color="neutral.500"
      bg="transparent"
      border="none"
      cursor="pointer"
      transition="background-color 120ms cubic-bezier(0.23, 1, 0.32, 1), color 120ms cubic-bezier(0.23, 1, 0.32, 1), transform 100ms cubic-bezier(0.23, 1, 0.32, 1)"
      _hover={{ bg: "neutral.100", color: "error.600" }}
      _active={{ transform: "scale(0.96)" }}
    >
      <Icon as={TbX} boxSize="14px" />
    </Box>
  );

  return (
    <>
      {/* Header — resource title + remove-whole-resource. Status and duration
          live on the per-account sub-rows below (each account is its own
          request), so the resource line spans the first three columns rather
          than leaving Status/Duration empty here. */}
      <Box
        display="grid"
        gridTemplateColumns={compact ? GRID_TEMPLATE_COMPACT : GRID_TEMPLATE}
        gap="10px"
        alignItems="center"
        px="md"
        py="2.5"
        fontSize="smd"
      >
        <Box gridColumn={compact ? "1 / 3" : "1 / 4"} minW={0}>
          <ResourceCell
            item={item}
            identityLine={
              <IdentityLine
                options={options}
                selectedIds={selectedIds}
                onToggle={onToggle}
                open={open}
                onOpenChange={onOpenChange}
              />
            }
          />
        </Box>
        <Flex justify="flex-end">
          {removeButton(`Remove ${catalogLinkName(item.resource)}`, onRemoveResource)}
        </Flex>
      </Box>

      {/* One sub-row per selected account — a full grid row (account / status /
          duration / remove) so it lines up with the table columns above. */}
      {selectedOpts.map((opt) => (
        <Fragment key={opt.id}>
          <Box
            display="grid"
            gridTemplateColumns={
              compact ? GRID_TEMPLATE_COMPACT : GRID_TEMPLATE
            }
            gap="10px"
            alignItems={compact ? "flex-start" : "center"}
            px="md"
            pb="2.5"
            fontSize="smd"
          >
            <Flex align="center" gap="sm" minW={0} pl="26px">
              <SourceIcon src={opt.imageUrl} />
              <Box
                minW={0}
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                color="neutral.900"
                fontWeight={500}
              >
                {opt.label}
              </Box>
            </Flex>
            <StatusDurationCells
              compact={compact}
              status={<StatusPill tone={tone} />}
              duration={accountDuration(opt.id)}
            />
            <Flex justify="flex-end">
              {removeButton(`Remove ${opt.label}`, () => onToggle(opt.id))}
            </Flex>
          </Box>
          {accountRoles?.(opt.id)}
        </Fragment>
      ))}

      {roles}

      {perItem && (
        <Box pl="md" pr={compact ? "42px" : "54px"} pb="2.5">
          <ReasonField
            required={reasonRequired}
            error={showReasonError}
            value={cfg.reason}
            onChange={onReasonChange}
            onBlur={onReasonBlur}
            rows={2}
            barText="A reason is required for this resource."
            placeholder={
              reasonRequired ? "Add a reason for the approver" : "Add reasoning…"
            }
          />
        </Box>
      )}
    </>
  );
};

export default BasketTable;
